import React, { useEffect, useState, useRef } from "react";
import { ResponsiveContainer } from "recharts";
import { BarChart3 } from "lucide-react";



const DefectTable = () => {
  const [defectData, setDefectData] = useState([]);
  const [filteredDefects, setFilteredDefects] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDefects, setSelectedDefects] = useState(new Set());
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectedSizes, setSelectedSizes] = useState(new Set());
  // const [analysis, setAnalysis] = useState(""); // State for storing analysis result

  const dropdownRef = useRef(null);

  // Fetch
  useEffect(() => {
    const fetchDefectData = async () => {
      try {
        const response = await fetch("https://eyonyjgai6.execute-api.us-east-1.amazonaws.com/defects/today"); // Replace with actual API Gateway URL
        const data = await response.json();

        setDefectData(data);
        setFilteredDefects(data);
      } catch (error) {
        console.error("Error fetching defect data:", error);
      }
    };

    fetchDefectData();
  }, []);

  /*** 
  
  // Fetch analysis data from DynamoDB
  const fetchAnalysisFromDynamoDB = async () => {
    try {
      const params = {
        TableName: "DefectAnalysis",
        Key: {
          id: { S: "1" },
        },
      };

      const result = await dynamoDB.getItem(params).promise();

      if (result && result.Item && result.Item.analysis) {
        setAnalysis(result.Item.analysis.S);
      } else {
        console.error("No analysis found in the DefectAnalysis table.");
        setAnalysis("Analysis data is not available.");
      }
    } catch (error) {
      console.error("Error fetching analysis from DynamoDB:", error);
      setAnalysis("Error fetching analysis data.");
    }
  };

*/

  // Toggle dropdown visibility
  const toggleDropdown = () => setShowDropdown(!showDropdown);

  // Handle checkbox filter changes
  const handleDefectFilterChange = (defect) => {
    setSelectedDefects((prev) => {
      const newFilters = new Set(prev);
      newFilters.has(defect)
        ? newFilters.delete(defect)
        : newFilters.add(defect);

      const filtered = newFilters.size
        ? defectData.filter(({ defect: defectName }) =>
            newFilters.has(defectName)
          )
        : defectData;

      setFilteredDefects(filtered);
      return newFilters;
    });
  };

  const handleProductFilterChange = (product) => {
    setSelectedProducts((prev) => {
      const newFilters = new Set(prev);
      newFilters.has(product)
        ? newFilters.delete(product)
        : newFilters.add(product);

      // Clear selected defects when the product filter changes
      setSelectedDefects(new Set());

      // Filter defects by selected product and size
      const filtered = defectData.filter(
        ({ product: productName, size: sizeName }) =>
          (newFilters.has(productName) || newFilters.size === 0) &&
          (selectedSizes.has(sizeName) || selectedSizes.size === 0)
      );

      setFilteredDefects(filtered);
      return newFilters;
    });
  };

  const handleSizeFilterChange = (size) => {
    setSelectedSizes((prev) => {
      const newFilters = new Set(prev);
      newFilters.has(size) ? newFilters.delete(size) : newFilters.add(size);

      // Clear selected defects when the size filter changes
      setSelectedDefects(new Set());

      // Filter defects by selected product and size
      const filtered = defectData.filter(
        ({ product: productName, size: sizeName }) =>
          (selectedProducts.has(productName) || selectedProducts.size === 0) &&
          (newFilters.has(sizeName) || newFilters.size === 0)
      );

      setFilteredDefects(filtered);
      return newFilters;
    });
  };
  // Close dropdown if clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Dynamic defect list based on selected filters
  const filteredDefectList = defectData.filter(
    ({ product, size }) =>
      (selectedProducts.has(product) || selectedProducts.size === 0) &&
      (selectedSizes.has(size) || selectedSizes.size === 0)
  );

  return (
    <div>
      {/* Title and Filter Button */}
      <div className="flex items-center justify-between mb-2 2xl:mb-4">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 2xl:h-5 w-4 2xl:w-5 text-blue-400" />
          <h2 className="text-sm 2xl:text-base font-semibold text-slate-200">
            Real-Time Defect Trend Analysis
          </h2>
        </div>

        {/* Filter Dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            onClick={toggleDropdown}
            className="px-4 py-2 mr-2 text-slate-400 bg-slate-900/60 text-xs border border-slate-800/60 rounded-2xl hover:bg-slate-800/60 transition-colors hover:text-blue-400 focus:z-10 focus:ring-2 focus:ring-blue-400 focus:text "
          >
            Filter Defects
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div className="absolute mt-2 right-0 bg-gray-800 text-white rounded shadow-lg w-48 z-10">
              <div className="p-2 max-h-52 overflow-y-auto">
                <h4 className="font-semibold mb-2 ">Products</h4>
                {Array.from(
                  new Set(defectData.map(({ product }) => product))
                ).map((product) => (
                  <label
                    key={product}
                    className="flex items-center p-2 hover:bg-gray-700 text-xs rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProducts.has(product)}
                      onChange={() => handleProductFilterChange(product)}
                      className="form-checkbox h-4 w-4 text-blue-500 mr-2"
                    />
                    {product}
                  </label>
                ))}

                <h4 className="font-semibold mb-2 mt-4">Sizes</h4>
                {Array.from(new Set(defectData.map(({ size }) => size))).map(
                  (size) => (
                    <label
                      key={size}
                      className="flex items-center p-2 hover:bg-gray-700 text-xs rounded"
                    >
                      <input
                        type="checkbox"
                        checked={selectedSizes.has(size)}
                        onChange={() => handleSizeFilterChange(size)}
                        className="form-checkbox h-4 w-4 text-blue-500 mr-2"
                      />
                      {size}
                    </label>
                  )
                )}

                <h4 className="font-semibold mb-2 mt-4">Defects</h4>
                {filteredDefectList.map(({ defect }) => (
                  <label
                    key={defect}
                    className="flex items-center p-2 hover:bg-gray-700 text-xs rounded"
                  >
                    <input
                      type="checkbox"
                      checked={selectedDefects.has(defect)}
                      onChange={() => handleDefectFilterChange(defect)}
                      className="form-checkbox h-4 w-4 text-blue-500 mr-2"
                    />
                    {defect}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Defect Table */}
      <div className="h-[250px] sm:h-[300px] 2xl:h-[310px]">
        <ResponsiveContainer width="100%" height="100%">
          <div
            className="relative overflow-x-auto overflow-y-auto scroll-smooth shadow-md h-full"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#1f2937 transparent",
            }}
          >
            <table className="w-full text-sm text-left mt-5 text-gray-400">
              <thead className="text-xs uppercase border-b border-t border-slate-800/60 text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    Defect Name
                  </th>
                  <th scope="col" className="px-4 py-3">
                    Quantity
                  </th>
                  <th scope="col" className="px-4 py-3 text-center">
                    Product
                  </th>
                  <th scope="col" className="px-4 py-3 hidden sm:table-cell">
                    Size
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredDefects.map(({ product, defect, count, size }) => (
                  <tr
                    key={`${product}-${defect}`}
                    className="border-b border-slate-800/60 hover:bg-slate-900/80"
                  >
                    <td className="px-4 py-4">{defect}</td>
                    <td className="px-4 py-4 text-center">{count}</td>

                    <th scope="row" className="px-4 py-4 ">
                      {product}
                    </th>
                    <td className="px-4 py-4 hidden sm:table-cell">{size}</td>
                  </tr>
                ))}
              </tbody>
              {/*  <caption className="p-4 caption-top text-lg font-semibold text-left text-blue-400 bg-slate-900/70">
                AI-Powered Insights
                <p className="mt-0.5 text-sm font-normal text-gray-300">
                  {analysis}{" "}
                </p>
              </caption> */}
            </table>
          </div>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DefectTable;
